import type { UIMessageChunk } from "ai"
import { parseJsonEventStream } from "@ai-sdk/provider-utils"
import { z } from "zod/v4"

import type { JSONValue, SharedV2ProviderMetadata } from "@ai-sdk/provider"
import type { ParseResult } from "@ai-sdk/provider-utils"
import { installTextDecoderStreamPolyfill } from "./text-decoder-stream-polyfill"

export type ProviderMetadata = SharedV2ProviderMetadata

/// https://github.com/vercel/ai/blob/f30012565bb82871554098960d10ba838b55645e/packages/ai/src/types/json-value.ts
export const jsonValueSchema: z.ZodType<JSONValue> = z.lazy(() =>
	z.union([
		z.null(),
		z.string(),
		z.number(),
		z.boolean(),
		z.record(z.string(), jsonValueSchema),
		z.array(jsonValueSchema),
	]),
)

/// https://github.com/vercel/ai/blob/main/packages/ai/src/types/provider-metadata.ts
export const providerMetadataSchema: z.ZodType<ProviderMetadata> = z.record(
	z.string(),
	z.record(z.string(), jsonValueSchema),
)

/// https://github.com/vercel/ai/blob/f30012565bb82871554098960d10ba838b55645e/packages/ai/src/ui-message-stream/ui-message-chunks.ts
export const uiMessageChunkSchema = z.union([
	z.strictObject({
		type: z.literal("text-start"),
		id: z.string(),
		providerMetadata: providerMetadataSchema.optional(),
	}),
	z.strictObject({
		type: z.literal("text-delta"),
		id: z.string(),
		delta: z.string(),
		providerMetadata: providerMetadataSchema.optional(),
	}),
	z.strictObject({
		type: z.literal("text-end"),
		id: z.string(),
		providerMetadata: providerMetadataSchema.optional(),
	}),
	z.strictObject({
		type: z.literal("error"),
		errorText: z.string(),
	}),
	z.strictObject({
		type: z.literal("tool-input-start"),
		toolCallId: z.string(),
		toolName: z.string(),
		providerExecuted: z.boolean().optional(),
		dynamic: z.boolean().optional(),
	}),
	z.strictObject({
		type: z.literal("tool-input-delta"),
		toolCallId: z.string(),
		inputTextDelta: z.string(),
	}),
	z.strictObject({
		type: z.literal("tool-input-available"),
		toolCallId: z.string(),
		toolName: z.string(),
		input: z.unknown(),
		providerExecuted: z.boolean().optional(),
		providerMetadata: providerMetadataSchema.optional(),
		dynamic: z.boolean().optional(),
	}),
	z.strictObject({
		type: z.literal("tool-output-available"),
		toolCallId: z.string(),
		output: z.unknown(),
		providerExecuted: z.boolean().optional(),
		dynamic: z.boolean().optional(),
	}),
	z.strictObject({
		type: z.literal("tool-output-error"),
		toolCallId: z.string(),
		errorText: z.string(),
		providerExecuted: z.boolean().optional(),
		dynamic: z.boolean().optional(),
	}),
	z.strictObject({
		type: z.literal("reasoning"),
		text: z.string(),
		providerMetadata: providerMetadataSchema.optional(),
	}),
	z.strictObject({
		type: z.literal("reasoning-start"),
		id: z.string(),
		providerMetadata: providerMetadataSchema.optional(),
	}),
	z.strictObject({
		type: z.literal("reasoning-delta"),
		id: z.string(),
		delta: z.string(),
		providerMetadata: providerMetadataSchema.optional(),
	}),
	z.strictObject({
		type: z.literal("reasoning-end"),
		id: z.string(),
		providerMetadata: providerMetadataSchema.optional(),
	}),
	z.strictObject({
		type: z.literal("reasoning-part-finish"),
	}),
	z.strictObject({
		type: z.literal("source-url"),
		sourceId: z.string(),
		url: z.string(),
		title: z.string().optional(),
		providerMetadata: providerMetadataSchema.optional(),
	}),
	z.strictObject({
		type: z.literal("source-document"),
		sourceId: z.string(),
		mediaType: z.string(),
		title: z.string(),
		filename: z.string().optional(),
		providerMetadata: providerMetadataSchema.optional(),
	}),
	z.strictObject({
		type: z.literal("file"),
		url: z.string(),
		mediaType: z.string(),
		providerMetadata: providerMetadataSchema.optional(),
	}),
	z.strictObject({
		type: z.string().startsWith("data-"),
		id: z.string().optional(),
		data: z.unknown(),
		transient: z.boolean().optional(),
	}),
	z.strictObject({
		type: z.literal("start-step"),
	}),
	z.strictObject({
		type: z.literal("finish-step"),
	}),
	z.strictObject({
		type: z.literal("start"),
		messageId: z.string().optional(),
		messageMetadata: z.unknown().optional(),
	}),
	z.strictObject({
		type: z.literal("finish"),
		messageMetadata: z.unknown().optional(),
	}),
	z.strictObject({
		type: z.literal("abort"),
	}),
	z.strictObject({
		type: z.literal("message-metadata"),
		messageMetadata: z.unknown(),
	}),
])

export class AiUiMessageChunkParser extends TransformStream<
	ParseResult<UIMessageChunk>,
	UIMessageChunk
> {
	constructor(onSkippedChunk?: (error: unknown) => void) {
		super({
			transform(chunk, controller) {
				if (!chunk.success) {
					console.warn("Skipping unparseable stream chunk:", chunk.error)
					onSkippedChunk?.(chunk.error)
					return
				}
				controller.enqueue(chunk.value)
			},
		})
	}
}

export const parseAiStream = (
	stream: ReadableStream<Uint8Array<ArrayBufferLike>>,
	onSkippedChunk?: (error: unknown) => void,
) => {
	installTextDecoderStreamPolyfill()

	return parseJsonEventStream({
		stream,
		schema: uiMessageChunkSchema,
	}).pipeThrough(new AiUiMessageChunkParser(onSkippedChunk))
}
